import { useEffect, useState } from 'react';
import { ArrowClockwise, ArrowSquareOut, Package, Printer, WarningCircle, Wrench } from 'shared/icons';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { Id } from '../../components/ui/Mono';
import {
    CollectorEmployeeProvider,
    useCollectorEmployee,
} from '../../components/warehouse/CollectorEmployeeContext';
import CollectorBadgeIdentify from '../../components/warehouse/collector/CollectorBadgeIdentify';
import { useKiosk } from '../../components/warehouse/collector/KioskContext';
import { useKioskFullscreen } from '../../components/warehouse/collector/useKioskFullscreen';
import ProductionMlbBench from '../../components/warehouse/production/ProductionMlbBench';
import { useProductionLabels } from '../../components/warehouse/production/productionLabels';
import { DescribePrinter, useZplPrinters, ZplPrinterSelect } from '../../components/warehouse/zplPrinting';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import EnvioPackingBench from '../../components/coletas/EnvioPackingBench';
import { useAuth } from '../../hooks/useAuth';
import { NormalizeOperatorRoles, OperatorCan } from '../../utils/operatorRoles';

/**
 * BANCADA — tela de DESKTOP (monitor 1280×1024 / 17" das bancadas), não mais um
 * modo do coletor mobile. É a estação das bancadas de FÁBRICA e de PACKING: o
 * crachá se identifica (o MESMO `CollectorBadgeIdentify` do coletor — um lugar
 * só pra consertar) e o terminal abre o trabalho do papel:
 *   · `fabrica` → bancada de produção (apontamento por bipe do MLB).
 *   · `packing` → EMBALAR o envio: bipa o EAN, o sistema acha o envio, e a
 *     etiqueta sai quando o pacote está embalado (o fluxo do zpl-checkout).
 *
 * SÃO DUAS, SÓ (decisão do dono, 03/09). A "conferência de carga do caminhão"
 * saiu da tela: a operação não faz isso hoje. O que o romaneio gera é uma OS
 * de SEPARAÇÃO PARA ENVIO, que o operador executa no COLETOR como qualquer
 * outra OS — não é bancada.
 *
 * NÃO EXISTE "trocar bancada": operador real tem UM papel; quem vê as duas é
 * admin. Trocar = deslogar e bipar o crachá de novo, que é a ação que a tela
 * já oferece e deixa a trilha honesta sobre quem fez o quê.
 *
 * TELA INICIAL (logo depois do crachá, SEMPRE — mesmo para quem tem um papel
 * só): em cima, a IMPRESSORA do MELIDASH neste computador; embaixo, a bancada.
 * As duas opções (Fábrica e Packing) só aparecem para quem tem os dois papéis
 * ou é supervisor. A impressora é escolhida AQUI porque as telas de bipe
 * prendem o foco no campo do leitor e nenhum outro campo convive com elas.
 *
 * TELA: no quiosque (/bancada) a estação vive em TELA CHEIA, sem botão para
 * sair — toda interação a devolve (useKioskFullscreen). Barra de endereço e
 * abas fora do caminho é o que evita o toque errado no meio do turno. A
 * saída para o sistema normal é o botão "Melidash" da tela do crachá: sai da
 * tela cheia e abre o login em outra aba.
 */
const BenchStation = () => {
    const { label } = useProductionLabels();
    // O seletor de impressora usa o dicionário do armazém.
    const { label: warehouseLabel } = useWarehouseLabels();
    const { user } = useAuth();
    const { employee, clear } = useCollectorEmployee();
    const [benchMode, setBenchMode] = useState(null); // 'fabrica' | 'embalar'
    const quiosque = useKiosk();
    useKioskFullscreen();

    const identified = Boolean(employee);
    const roles = employee?.roles;
    const canFabrica = identified && OperatorCan(roles, 'production');
    const canPacking = identified && OperatorCan(roles, 'shipping');
    const canBoth = canFabrica && canPacking;
    // A bancada só abre depois da tela inicial (impressora + bancada). Quem tem
    // um papel só vê uma opção; a escolha entre as duas é de quem tem os dois
    // papéis ou é supervisor.
    const activeMode = benchMode;
    const onStartScreen = identified && (canFabrica || canPacking) && !activeMode;
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(onStartScreen);

    // Trocar de crachá zera a escolha de bancada.
    useEffect(() => {
        if (!identified) {
            setBenchMode(null);
        }
    }, [identified]);

    // Saída da bancada para o sistema normal: sai da tela cheia (o botão é
    // `data-sem-tela-cheia`, então o clique não a devolve) e abre o login em
    // OUTRA aba — a bancada continua aberta nesta. O botão solta o foco: com
    // ele focado, o Enter que fecha o bipe do crachá o apertaria de novo (e o
    // bipe cairia num elemento que não devolve a tela cheia).
    const openMelidash = (event) => {
        event?.currentTarget?.blur?.();
        try {
            if (document.fullscreenElement) {
                Promise.resolve(document.exitFullscreen()).catch(() => {});
            }
        } catch {
            // sem tela cheia para sair: a aba nova abre igual
        }
        window.open('/login', '_blank', 'noopener');
    };

    const changeOperator = () => {
        setBenchMode(null);
        clear();
    };

    return (
        <div className="flex min-h-dvh w-full flex-col gap-3 p-2">
            {/* Cabeçalho da ESTAÇÃO — onde você está e quem é o crachá. */}
            <header className="flex items-center justify-between gap-4 rounded-2xl border border-mono-200 bg-white px-6 py-4 dark:border-mono-800 dark:bg-mono-950">
                <div className="flex items-center gap-3">
                    {activeMode === 'embalar' ? (
                        <Package size={30} className="text-black dark:text-white" />
                    ) : (
                        <Wrench size={30} className="text-black dark:text-white" />
                    )}
                    <div>
                        <h1 className="text-2xl font-black leading-none text-black dark:text-white">
                            {activeMode === 'embalar'
                                ? label('wms.bench.station_title_packing')
                                : activeMode === 'fabrica'
                                  ? label('wms.bench.station_title_fabrica')
                                  : label('wms.bench.station_title')}
                        </h1>
                        <p className="mt-1 text-xs uppercase tracking-wide text-mono-500">
                            {label('wms.collector.device')}: {user?.email || '—'}
                        </p>
                    </div>
                </div>
                {identified ? (
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-lg font-bold leading-none text-black dark:text-white">
                                {employee.employee_name}
                            </p>
                            <div className="mt-1 flex items-center justify-end gap-2">
                                <Id value={employee.employee_code} />
                                {NormalizeOperatorRoles(roles).map((r) => (
                                    <Badge key={r} variant={r === 'supervisor' ? 'info' : 'default'}>
                                        {label(`wms.operator_role.${r}`)}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        {/* Sem "trocar bancada": operador real tem UM papel, e trocar é
                            deslogar e bipar o crachá de novo. */}
                        <Button variant="secondary" size="lg" onClick={changeOperator}>
                            <ArrowClockwise size={20} className="mr-2" />
                            {label('wms.collector.change_operator')}
                        </Button>
                    </div>
                ) : quiosque ? (
                    // Só na tela do crachá (ninguém identificado): no meio do
                    // turno a saída para o sistema normal não aparece.
                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={openMelidash}
                        title={label('wms.bench.open_melidash_hint')}
                        data-sem-tela-cheia
                    >
                        {label('wms.bench.open_melidash')}
                        <ArrowSquareOut size={20} className="ml-2" />
                    </Button>
                ) : null}
            </header>

            <main className="flex w-full flex-1 items-start justify-center">
                {!identified ? (
                    <div className="mt-8 w-full max-w-md rounded-2xl border border-mono-200 bg-white p-8 dark:border-mono-800 dark:bg-mono-950">
                        <CollectorBadgeIdentify label={label} />
                    </div>
                ) : !canFabrica && !canPacking ? (
                    // Sem papel de bancada: explica, não culpa o operador.
                    <div className="mt-8 w-full max-w-md space-y-5 rounded-2xl border border-warning-300 bg-warning-soft p-8 text-center dark:border-warning-800 dark:bg-warning-950">
                        <WarningCircle size={48} className="mx-auto text-warning-600 dark:text-warning-400" />
                        <p className="text-lg font-semibold text-warning-text dark:text-warning-200">
                            {label('wms.bench.no_bench_role')}
                        </p>
                        <Button variant="secondary" size="lg" className="w-full" onClick={changeOperator}>
                            <ArrowClockwise size={20} className="mr-2" />
                            {label('wms.collector.change_operator')}
                        </Button>
                    </div>
                ) : !activeMode ? (
                    // TELA INICIAL: em cima a impressora (do MELIDASH, neste
                    // computador), embaixo a bancada do papel do crachá.
                    <div className="mt-4 w-full max-w-4xl space-y-4">
                        <section className="space-y-3 rounded-2xl border border-mono-200 bg-white p-6 dark:border-mono-800 dark:bg-mono-950">
                            <h2 className="flex items-center gap-2 text-xl font-bold text-black dark:text-white">
                                <Printer size={26} />
                                {label('wms.bench.printer_title')}
                            </h2>
                            <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.bench.printer_help')}</p>
                            <ZplPrinterSelect
                                label={warehouseLabel}
                                printers={printers}
                                localPrinters={localPrinters}
                                value={selectedPrinter}
                                onChange={setSelectedPrinter}
                            />
                            {selectedPrinter ? (
                                <p className="text-sm font-semibold text-success-700 dark:text-success-400">
                                    {label('wms.bench.printer_current').replace(
                                        '{printer}',
                                        DescribePrinter(selectedPrinter, printers),
                                    )}
                                </p>
                            ) : (
                                <p className="flex items-center gap-2 text-sm font-semibold text-warning-text dark:text-warning-300">
                                    <WarningCircle size={18} className="shrink-0" />
                                    {label('wms.bench.printer_none')}
                                </p>
                            )}
                        </section>

                        <section className="space-y-3 rounded-2xl border border-mono-200 bg-white p-6 dark:border-mono-800 dark:bg-mono-950">
                            <h2 className="text-center text-xl font-bold text-black dark:text-white">
                                {label(canBoth ? 'wms.bench.choose_bench' : 'wms.bench.your_bench')}
                            </h2>
                            <div className={`grid gap-4 ${canBoth ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                {canFabrica ? (
                                    <button
                                        type="button"
                                        onClick={() => setBenchMode('fabrica')}
                                        className="flex flex-col items-center gap-3 rounded-2xl border-2 border-mono-300 bg-white px-6 py-10 transition-colors hover:border-black dark:border-mono-700 dark:bg-mono-950 dark:hover:border-white"
                                    >
                                        <Wrench size={44} className="text-black dark:text-white" />
                                        <span className="text-2xl font-black text-black dark:text-white">
                                            {label('wms.bench.bench_fabrica')}
                                        </span>
                                    </button>
                                ) : null}
                                {canPacking ? (
                                    <button
                                        type="button"
                                        onClick={() => setBenchMode('embalar')}
                                        className="flex flex-col items-center gap-3 rounded-2xl border-2 border-mono-300 bg-white px-6 py-10 transition-colors hover:border-black dark:border-mono-700 dark:bg-mono-950 dark:hover:border-white"
                                    >
                                        <Package size={44} className="text-black dark:text-white" />
                                        <span className="text-2xl font-black text-black dark:text-white">
                                            {label('wms.bench.bench_packing')}
                                        </span>
                                        <span className="text-xs text-mono-500">
                                            {label('wms.bench.bench_packing_hint')}
                                        </span>
                                    </button>
                                ) : null}
                            </div>
                        </section>
                    </div>
                ) : activeMode === 'fabrica' ? (
                    <div className="w-full">
                        <ProductionMlbBench label={label} operator={employee} onExit={changeOperator} />
                    </div>
                ) : (
                    <div className="w-full">
                        <EnvioPackingBench operator={employee} />
                    </div>
                )}
            </main>
        </div>
    );
};

const WarehouseBenchStation = () => (
    <CollectorEmployeeProvider>
        <BenchStation />
    </CollectorEmployeeProvider>
);

export default WarehouseBenchStation;
